import React, { useState, useEffect } from "react";
import MemberAreaFooter from "../components/MemberAreaFooter";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  Play, Lock, Clock, Users, Tag, ExternalLink, ArrowLeft, Calendar, 
  AlertTriangle, RefreshCw, Instagram, Twitter, Globe, Heart, Film, 
  UserRound, MessageCircle, Send, Crown, Share2, Bookmark, Eye,
  Check, Sparkles, Star, ChevronLeft, ChevronRight, ThumbsUp, Camera, Award,
  Zap, TrendingUp, X as CloseIcon, Loader
} from "lucide-react";
import MuxPlayer from "@mux/mux-player-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { getPublicTrailerPlaybackId, describeMediaError } from "../lib/publicTrailer";
import { getThumbnailSources } from "../lib/freeArea";
import { getVideoCategories } from "../lib/videoTags";
import { videoCast } from "../lib/videoCast";
import FeaturedVideoCast from "../components/FeaturedVideoCast";
import AdSlot from "../components/AdSlot";

export default function FreeVideoPreview() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cast, setCast] = useState([]);
  const [crew, setCrew] = useState([]);
  const [studio, setStudio] = useState(null);
  const [trailerError, setTrailerError] = useState(false);
  const [trailerRetry, setTrailerRetry] = useState(0);
  const [teaserError, setTeaserError] = useState(false);
  const [teaserRetry, setTeaserRetry] = useState(0);
  const [generatedError, setGeneratedError] = useState(false);
  const [generatedRetry, setGeneratedRetry] = useState(0);
  const [likes, setLikes] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [liking, setLiking] = useState(false);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(null);

  useEffect(() => {
    loadVideo();
    loadEngagement();
    loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  useEffect(() => {
    if (video) {
      loadGalleryPhotos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video]);

  useEffect(() => {
    if (activeGalleryIndex === null || !galleryPhotos.length) return undefined;
    const handleGalleryKeyDown = (event) => {
      if (event.key === "Escape") setActiveGalleryIndex(null);
      if (event.key === "ArrowLeft") setActiveGalleryIndex((index) => (index - 1 + galleryPhotos.length) % galleryPhotos.length);
      if (event.key === "ArrowRight") setActiveGalleryIndex((index) => (index + 1) % galleryPhotos.length);
    };
    window.addEventListener("keydown", handleGalleryKeyDown);
    return () => window.removeEventListener("keydown", handleGalleryKeyDown);
  }, [activeGalleryIndex, galleryPhotos.length]);

  const loadVideo = async () => {
    try {
      const response = await api.get(`/api/videos/${videoId}/preview`);
      setVideo(response.data);
      
      setCast(videoCast(response.data));
      setCrew(response.data.crew || (response.data.cast_crew || []).filter(
        (person) => person.role && !/model|performer|talent|actor/i.test(person.role)
      ));
      setStudio(null);
      setGalleryPhotos([]);
      setActiveGalleryIndex(null);
      setTrailerError(false);
      setTrailerRetry(0);
      setTeaserError(false);
      setTeaserRetry(0);
      setGeneratedError(false);

      if (response.data.crew && response.data.crew.length > 0) {
        setCrew(response.data.crew);
      }
      
      if (response.data.studio_crm_id) {
        try {
          const studioRes = await api.get(`/api/crm/studios/${response.data.studio_crm_id}`);
          setStudio(studioRes.data);
        } catch (err) {
          console.error("Failed to load studio:", err);
        }
      }

      // Load related videos
      try {
        const relatedRes = await api.get("/api/member-content/tier/free/videos");
        setRelatedVideos((relatedRes.data.videos || []).filter(v => v.id !== videoId).slice(0, 4));
      } catch (err) {
        console.error("Failed to load related videos:", err);
      }

      setLoading(false);
    } catch (error) {
      console.error("Failed to load video:", error);
      toast.error("Failed to load video");
      setLoading(false);
    }
  };

  const loadEngagement = async () => {
    try {
      const response = await api.get(`/api/videos/${videoId}/likes`);
      setLikes(response.data.likes || 0);
      setHasLiked(localStorage.getItem(`free-preview-liked:${videoId}`) === "1");
    } catch (err) {
      console.error("Failed to load engagement:", err);
    }
  };

  const loadComments = async () => {
    try {
      const response = await api.get(`/api/videos/${videoId}/comments`);
      setComments(response.data.comments || []);
    } catch (err) {
      console.error("Failed to load comments:", err);
      setComments([]);
    }
  };

  const loadGalleryPhotos = async () => {
    // Get gallery photos from video data
    const photos = video?.gallery_photos?.length ? video.gallery_photos : video?.scene_photos;
    if (photos?.length) {
      setGalleryPhotos(photos.map((photo) => typeof photo === "string" ? photo : photo.url || photo.image_url).filter(Boolean));
    } else if (video && video.thumbnail_url) {
      // Fallback to thumbnail if no gallery
      setGalleryPhotos([video.thumbnail_url]);
    }
  };

  const handleLike = async () => {
    if (liking || hasLiked) return;
    setLiking(true);
    try {
      const response = await api.post(`/api/videos/${videoId}/like`);
      setHasLiked(true);
      setLikes(response.data.likes ?? likes + 1);
      localStorage.setItem(`free-preview-liked:${videoId}`, "1");
      toast.success("Video liked!");
    } catch (error) {
      toast.error("Please login to like videos");
    } finally {
      setLiking(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: video?.title,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    try {
      const response = await api.post(`/api/videos/${videoId}/comments`, {
        video_id: videoId,
        content: newComment.trim()
      });
      
      if (response.data.comment) {
        toast.success("Comment posted!");
        setNewComment("");
        setComments((current) => [...current, response.data.comment]);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Please login to post comments");
      } else {
        toast.error("Failed to post comment");
      }
    }
  };

  const goToUpgrade = () => {
    navigate("/members/upgrade");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-[#FF10F0]/30 border-t-[#FF10F0] rounded-full animate-spin" />
          <div className="text-sm text-zinc-500 font-mono">Loading preview...</div>
        </div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle size={48} className="text-[#FF10F0] mx-auto mb-4" />
          <p className="text-zinc-400 mb-6">Video not found</p>
          <button
            onClick={() => navigate("/members/free")}
            className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
          >
            Back to Videos
          </button>
        </div>
      </div>
    );
  }

  const trailerPlaybackId = getPublicTrailerPlaybackId(video);
  const teaser = video.teaser && (!video.teaser.status || ["ready", "published"].includes(video.teaser.status))
    ? video.teaser : null;
  const teaserPlaybackId = teaser?.playback_id;
  const categories = getVideoCategories(video.tags || []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header Navigation */}
      <div className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/members/free"
              data-testid="back-to-videos-link"
              className="flex items-center gap-2 text-zinc-400 hover:text-[#FF10F0] transition-colors"
            >
              <ArrowLeft size={20} />
              <span className="text-sm font-medium">Back to Videos</span>
            </Link>
            
            <div className="flex items-center gap-3">
              <div className="px-3 py-1.5 bg-[#FF10F0]/10 border border-[#FF10F0]/30 rounded-full">
                <span className="text-xs font-bold text-[#FF10F0] uppercase tracking-wider">Preview</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-8 space-y-8">
            {/* Trailer and teaser are separate, independently failing public media items. */}
            <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2" data-testid="trailer-teaser-grid">
              {(trailerPlaybackId || !teaserPlaybackId) && (
                <section aria-labelledby="free-video-trailer-heading" aria-busy={loading} className="min-w-0 overflow-hidden rounded-2xl border border-[#FF10F0]/20 bg-[#0A0A0E] shadow-[0_0_32px_rgba(255,16,240,0.12)]" data-testid="video-player-preview-container">
                  <h2 id="free-video-trailer-heading" className="px-4 pt-4 text-base font-semibold text-[#FF10F0]">Trailer</h2>
                  {trailerPlaybackId && !trailerError ? (
                    <div className="relative mt-3 aspect-video">
                      <MuxPlayer
                        key={`${trailerPlaybackId}-${trailerRetry}`}
                        playbackId={trailerPlaybackId}
                        streamType="on-demand"
                        metadata={{ video_title: video.title, viewer_user_id: "preview-user" }}
                        aria-label={`Trailer for ${video.title}`}
                        onError={(event) => { console.error("Mux trailer player error:", event); setTrailerError(true); }}
                        className="h-full w-full"
                      />
                    </div>
                  ) : trailerPlaybackId ? (
                    <div role="alert" className="p-4 text-sm text-zinc-200">Trailer playback is unavailable or unsupported.<button type="button" className="ml-3 min-h-10 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF10F0]" onClick={() => { setTrailerError(false); setTrailerRetry((value) => value + 1); }}>Retry Trailer</button></div>
                  ) : teaserPlaybackId ? null : (
                    <div className="relative mt-3 aspect-video overflow-hidden bg-[radial-gradient(ellipse_at_30%_20%,rgba(225,29,72,.26),transparent_55%),linear-gradient(120deg,#170B18,#08090D)] p-5 sm:p-8">
                      <div className="flex items-center justify-between gap-3"><span className="text-xs font-bold uppercase tracking-[.18em] text-pink-200">FREE PREVIEW</span><span aria-label="E brand badge" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-sm font-bold">E</span></div>
                      <button type="button" aria-label="Play free video preview" onClick={() => toast.error("Trailer preview unavailable")} className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/10 transition hover:bg-white/20"><Play size={24} /></button>
                      <div className="absolute bottom-5 left-5 right-5 sm:bottom-8 sm:left-8 sm:right-8"><h2 className="line-clamp-2 text-xl font-bold sm:text-3xl">{video.title}</h2><p className="mt-2 text-xs text-zinc-200">{video.runtime_minutes ? `${video.runtime_minutes}m · ` : video.duration ? `${Math.floor(video.duration / 60)}m · ` : ""}Trailer · {new Date(video.shoot_date || video.created_at || Date.now()).getFullYear()}</p></div>
                    </div>
                  )}
                </section>
              )}
              {teaserPlaybackId && (
                <section aria-labelledby="free-video-teaser-heading" aria-busy={loading} className="min-w-0 overflow-hidden rounded-2xl border border-[#FF10F0]/20 bg-[#0A0A0E] shadow-[0_0_32px_rgba(255,16,240,0.12)]" data-testid="video-teaser-preview-container">
                  <div className="flex min-w-0 items-start justify-between gap-3 px-4 pt-4">
                    <div className="min-w-0">
                      <h2 id="free-video-teaser-heading" className="text-base font-semibold text-[#FF10F0]">Teaser</h2>
                      {teaser.title && teaser.title !== "Teaser" && <p className="truncate text-sm text-zinc-300">{teaser.title}</p>}
                    </div>
                    {teaser.duration > 0 && <span className="shrink-0 text-xs text-zinc-400">{Math.round(teaser.duration)} sec</span>}
                  </div>
                  {teaser.description && <p className="px-4 pt-1 text-sm text-zinc-300">{teaser.description}</p>}
                  {teaserError ? (
                    <div role="alert" className="p-4 text-sm text-zinc-200">Teaser playback is unavailable or unsupported.<button type="button" className="ml-3 min-h-11 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF10F0]" onClick={() => { setTeaserError(false); setTeaserRetry((value) => value + 1); }}>Retry Teaser</button></div>
                  ) : (
                    <div className="relative mt-3 aspect-video bg-black">
                      <MuxPlayer key={`${teaserPlaybackId}-${teaserRetry}`} playbackId={teaserPlaybackId} poster={teaser.poster_url || undefined} streamType="on-demand" aria-label={`Teaser: ${teaser.title || video.title}`} onError={() => setTeaserError(true)} className="h-full w-full" />
                    </div>
                  )}
                </section>
              )}
            </div>

            {video.generated_trailer_mux_playback_id && (
              <section className="overflow-hidden rounded-2xl border border-white/10 bg-[#0F1118]">
                <div className="p-4"><h2 className="font-semibold">App-generated teaser</h2>{video.generated_trailer_mux_duration && <p className="mt-1 text-sm text-zinc-300">{Math.round(video.generated_trailer_mux_duration)} second clip</p>}</div>
                {generatedError ? <div data-testid="generated-trailer-error" className="p-5 text-sm text-zinc-200">This teaser could not be played.<button type="button" data-testid="generated-trailer-retry" className="ml-3 underline" onClick={() => { setGeneratedError(false); setGeneratedRetry((value) => value + 1); }}>Retry</button></div> : <MuxPlayer key={`${videoId}-${generatedRetry}`} playbackId={video.generated_trailer_mux_playback_id} streamType="on-demand" onError={() => setGeneratedError(true)} className="aspect-video w-full" />}
              </section>
            )}

            {/* Video Metadata */}
            <div className="space-y-4" data-testid="video-title-header">
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{video.title}</h1>
              
              <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-400">
                {video.created_at && (
                  <div className="flex items-center gap-1.5">
                    <Calendar size={16} />
                    <span>{new Date(video.created_at).toLocaleDateString()}</span>
                  </div>
                )}
                {video.duration && (
                  <div className="flex items-center gap-1.5">
                    <Clock size={16} />
                    <span>{Math.floor(video.duration / 60)}min</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <Eye size={16} />
                  <span>{likes} likes</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={handleLike}
                  disabled={liking || hasLiked}
                  aria-pressed={hasLiked}
                  data-testid="like-video-button"
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                    hasLiked
                      ? "bg-[#FF10F0]/20 border-[#FF10F0] text-[#FF10F0]"
                      : "bg-white/5 border-white/10 hover:border-[#FF10F0]/50 text-zinc-300"
                  }`}
                >
                  <Heart size={18} className={hasLiked ? "fill-current" : ""} />
                  <span className="text-sm font-medium">{hasLiked ? "Liked" : "Like"} · {likes}</span>
                </button>

                <button
                  onClick={handleShare}
                  data-testid="share-video-button"
                  className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 hover:border-[#FF10F0]/50 rounded-full text-zinc-300 transition-all"
                >
                  <Share2 size={18} />
                  <span className="text-sm font-medium">Share</span>
                </button>

                {video.studio_url && (
                  <a
                    href={video.studio_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="view-on-studio-button"
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FF10F0] to-[#FF2D95] border border-[#FF10F0]/50 hover:shadow-[0_0_20px_rgba(255,16,240,0.4)] rounded-full text-white transition-all font-semibold"
                  >
                    <ExternalLink size={18} />
                    <span className="text-sm">View Full Scene</span>
                  </a>
                )}

                <button
                  className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 hover:border-[#FF10F0]/50 rounded-full text-zinc-300 transition-all"
                >
                  <Bookmark size={18} />
                  <span className="text-sm font-medium">Save</span>
                </button>
              </div>

              {/* Description */}
              {(video.free_description || video.description) && (
                <div className="space-y-3">
                  {video.free_description && (
                    <div className="p-4 bg-gradient-to-br from-[#FF10F0]/10 to-[#FF2D95]/10 border border-[#FF10F0]/30 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold text-[#FF10F0] uppercase tracking-wider">🆓 Free Members Preview</span>
                      </div>
                      <p className="text-sm text-white leading-relaxed">{video.free_description}</p>
                    </div>
                  )}
                  {video.description && (
                    <div className="p-4 bg-[#0A0A0E] border border-white/10 rounded-xl">
                      <p className="text-sm text-zinc-300 leading-relaxed">{video.description}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Where to Watch - Platform Links from CTA */}
            {(() => {
              const ctaButtons = (video.cta?.buttons || []).filter(b => b.visible && b.url && b.url.startsWith('http'));
              if (ctaButtons.length === 0) return null;

              // Platform color mapping
              const getPlatformColor = (label) => {
                const lowerLabel = label.toLowerCase();
                if (lowerLabel.includes('justforfans') || lowerLabel.includes('jff')) return '#00AEEF';
                if (lowerLabel.includes('onlyfans vip')) return '#00AFF0';
                if (lowerLabel.includes('onlyfans') && (lowerLabel.includes('free') || lowerLabel.includes('basic'))) return '#7C3AED';
                if (lowerLabel.includes('rawfuckclub') || lowerLabel.includes('rfc')) return '#E11D48';
                return '#FF10F0'; // Default pink neon
              };

              const getPlatformIcon = (label) => {
                const lowerLabel = label.toLowerCase();
                if (lowerLabel.includes('vip') || lowerLabel.includes('premium')) return Crown;
                if (lowerLabel.includes('free') || lowerLabel.includes('basic')) return Users;
                return Film;
              };

              return (
                <div className="space-y-4">
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <ExternalLink size={24} className="text-[#FF10F0]" />
                    {video.cta?.headline || "Watch Full Video On"}
                  </h2>
                  {video.cta?.text?.trim() && (
                    <p className="max-w-3xl text-sm sm:text-base text-zinc-300 leading-relaxed">
                      {video.cta.text}
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {ctaButtons.map((button, idx) => {
                      const color = getPlatformColor(button.label);
                      const Icon = getPlatformIcon(button.label);
                      
                      return (
                        <a
                          key={button.id || idx}
                          href={button.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group p-4 bg-gradient-to-br from-[#0A0A0E] to-black rounded-xl transition-all"
                          style={{
                            border: `1px solid ${color}30`,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.border = `1px solid ${color}`;
                            e.currentTarget.style.boxShadow = `0 0 20px ${color}33`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.border = `1px solid ${color}30`;
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-10 h-10 rounded-lg flex items-center justify-center"
                                style={{ backgroundColor: `${color}20` }}
                              >
                                <Icon size={20} style={{ color }} />
                              </div>
                              <div>
                                <h3 className="font-bold text-white">{button.label}</h3>
                              </div>
                            </div>
                            <ChevronRight 
                              size={18} 
                              className="text-zinc-600 transition-colors"
                              style={{ 
                                color: 'rgb(82, 82, 91)',
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.color = color}
                              onMouseLeave={(e) => e.currentTarget.style.color = 'rgb(82, 82, 91)'}
                            />
                          </div>
                          <p className="text-xs text-zinc-500">Click to watch • Full video</p>
                        </a>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <AdSlot placement="below_video" />
            <FeaturedVideoCast people={cast} visibleFields={video.cast_visibility_fields} />
            {crew.length > 0 && <FeaturedVideoCast people={crew} heading="Production Crew" />}
            {/* Linked Pictures Gallery */}
            {galleryPhotos.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Camera size={24} className="text-[#FF10F0]" />
                    Scene Gallery
                    <span className="text-sm font-normal text-zinc-500">({galleryPhotos.length} photos)</span>
                  </h2>
                  <button type="button" data-testid="gallery-view-all" aria-label={`View all ${galleryPhotos.length} scene gallery photos`}
                    onClick={() => setActiveGalleryIndex(0)} className="text-sm text-[#FF10F0] hover:underline flex items-center gap-1">
                    View All
                    <ChevronRight size={16} />
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {galleryPhotos.map((photo, idx) => (
                    <button
                      type="button"
                      key={idx}
                      data-testid={`photo-gallery-item-${idx}`}
                      aria-label={`View scene photo ${idx + 1} of ${galleryPhotos.length}`}
                      onClick={() => setActiveGalleryIndex(idx)}
                      className="group relative aspect-[4/5] rounded-xl overflow-hidden cursor-pointer border border-white/10 hover:border-[#FF10F0]/40 transition-all"
                    >
                      <img
                        src={photo}
                        alt={`Gallery ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-[#FF10F0] flex items-center justify-center">
                          <Eye size={20} className="text-white" />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Comments Section */}
            <div className="space-y-4">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <MessageCircle size={24} className="text-[#FF10F0]" />
                Comments
                <span className="text-sm font-normal text-zinc-500">({comments.length})</span>
              </h2>

              {/* Comment Input */}
              <form onSubmit={handleCommentSubmit} className="p-4 bg-[#0A0A0E] border border-white/10 rounded-xl">
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF10F0]/20 to-[#FF2D95]/20 flex items-center justify-center border border-[#FF10F0]/30 flex-shrink-0">
                    <UserRound size={20} className="text-[#FF10F0]" />
                  </div>
                  <div className="flex-1">
                    <textarea
                      id="preview-comment"
                      rows={3}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Share your thoughts..."
                      data-testid="comment-input"
                      className="w-full px-4 py-2 bg-black/50 border border-white/10 focus:border-[#FF10F0]/40 rounded-lg text-sm text-white placeholder-zinc-500 outline-none transition-colors"
                    />
                    <div className="flex items-center justify-end gap-2 mt-2">
                      <button
                        type="submit"
                        data-testid="comment-submit-button"
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FF10F0] to-[#FF2D95] text-white text-sm font-medium rounded-lg hover:shadow-[0_0_15px_rgba(255,16,240,0.4)] transition-all"
                      >
                        <Send size={16} />
                        Post
                      </button>
                    </div>
                  </div>
                </div>
              </form>

              {/* Comments List */}
              <div className="space-y-3">
                {comments.map((comment) => (
                  <div key={comment.id} className="p-4 bg-[#0A0A0E] border border-white/10 rounded-xl hover:border-white/20 transition-colors">
                    <div className="flex gap-3">
                      <img
                        src={comment.avatar}
                        alt={comment.user}
                        className="w-10 h-10 rounded-full object-cover border border-[#FF10F0]/30 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-white text-sm">{comment.user}</span>
                          <span className="text-xs text-zinc-500">{comment.timestamp}</span>
                        </div>
                        <p className="text-sm text-zinc-300 mb-2">{comment.content || comment.comment}</p>
                        <div className="flex items-center gap-4">
                          <button className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-[#FF10F0] transition-colors">
                            <ThumbsUp size={14} />
                            <span>{comment.likes}</span>
                          </button>
                          <button className="text-xs text-zinc-400 hover:text-[#FF10F0] transition-colors">
                            Reply
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            {categories.length > 0 && (
              <section aria-label="Video categories" data-testid="preview-categories-sidebar" className="space-y-3">
                <h3 className="text-lg font-bold">Categories</h3>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <span key={cat} className="rounded-full border border-white/10 bg-[#0A0A0E] px-3 py-1.5 text-xs text-zinc-300">
                      {cat}
                    </span>
                  ))}
                </div>
              </section>
            )}
            <AdSlot placement="sidebar" />
            {/* Related Videos */}
            {relatedVideos.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold">More Videos</h3>
                <div className="space-y-3">
                  {relatedVideos.map((relVideo) => {
                    const thumbnail = getThumbnailSources(relVideo)[0]?.src;
                    return (
                      <Link
                        key={relVideo.id}
                        to={`/video/${relVideo.id}/preview`}
                        data-testid="related-video-card"
                        className="group block p-3 bg-[#0A0A0E] border border-white/10 hover:border-[#FF10F0]/40 rounded-xl transition-all"
                      >
                        <div className="flex gap-3">
                          <div className="relative w-24 h-16 rounded-lg overflow-hidden bg-black flex-shrink-0">
                            {thumbnail ? (
                              <img
                                src={thumbnail}
                                alt={relVideo.title}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Play size={20} className="text-zinc-600" />
                              </div>
                            )}
                            <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-black/80 rounded text-xs text-[#FF10F0] font-bold">
                              TRAILER
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-white line-clamp-2 group-hover:text-[#FF10F0] transition-colors mb-1">
                              {relVideo.title}
                            </h4>
                            {relVideo.duration && (
                              <p className="text-xs text-zinc-500">{Math.floor(relVideo.duration / 60)} min</p>
                            )}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {activeGalleryIndex !== null && galleryPhotos[activeGalleryIndex] && (
        <div role="dialog" aria-modal="true" aria-label={`Scene gallery photo ${activeGalleryIndex + 1} of ${galleryPhotos.length}`}
          data-testid="gallery-lightbox" onClick={() => setActiveGalleryIndex(null)}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/95 p-4 sm:p-8">
          <div className="relative flex max-h-full w-full max-w-6xl flex-col items-center gap-3" onClick={(event) => event.stopPropagation()}>
            <div className="flex w-full items-center justify-between text-sm text-zinc-300">
              <span>{activeGalleryIndex + 1} / {galleryPhotos.length}</span>
              <button type="button" autoFocus aria-label="Close scene gallery" onClick={() => setActiveGalleryIndex(null)}
                className="rounded-full border border-white/20 p-2 text-white hover:bg-white/10"><CloseIcon size={20} /></button>
            </div>
            <div className="flex min-h-0 w-full flex-1 items-center justify-center gap-3">
              {galleryPhotos.length > 1 && <button type="button" aria-label="Previous photo"
                onClick={() => setActiveGalleryIndex((index) => (index - 1 + galleryPhotos.length) % galleryPhotos.length)}
                className="shrink-0 rounded-full border border-white/20 p-2 text-white hover:bg-white/10"><ChevronLeft size={22} /></button>}
              <img src={galleryPhotos[activeGalleryIndex]} alt={`Scene gallery photo ${activeGalleryIndex + 1}`}
                data-testid="gallery-lightbox-image" className="max-h-[78vh] max-w-[calc(100%-5rem)] object-contain" />
              {galleryPhotos.length > 1 && <button type="button" aria-label="Next photo"
                onClick={() => setActiveGalleryIndex((index) => (index + 1) % galleryPhotos.length)}
                className="shrink-0 rounded-full border border-white/20 p-2 text-white hover:bg-white/10"><ChevronRight size={22} /></button>}
            </div>
          </div>
        </div>
      )}
      <MemberAreaFooter />
    </div>
  );
}
